'use client';

import { useState } from 'react';

import { Cms } from '@ci/cms';
import { usePathname } from '@ci/i18n/navigation';
import { Collapsible } from '@ci/ui/collapsible';
import { SidebarMenuItem } from '@ci/ui/sidebar';
import { cn, isRouteActive } from '@ci/ui/utils';

export function DocsNavigationCollapsible(
  props: React.PropsWithChildren<{
    node: Cms.ContentItem;
    prefix: string;
  }>,
) {
  // The locale-aware pathname, as DocsNavLinkButton reads it: on `/es/docs/…`
  // the raw one never matches `/docs/<section>`, so the section a Spanish
  // reader is in would stay collapsed.
  const currentPath = usePathname();
  const prefix = props.prefix;

  /*
   * "Am I inside this section?" — its own overview page counts, not only its
   * children. PAY-4060 made the section label a link, so the overview page is
   * somewhere a reader can now land, and the comment below already promised
   * that navigating into a section re-opens it. A reader who clicked the
   * section and got a collapsed one would be reading the page while the nav
   * denied they were in it. It is also what the app's own sidebar does: the
   * group holding the current page is the one that opens.
   *
   * `isRouteActive` prefix-matches, but a section's own url
   * (`<section>/<overview>`) is never a prefix of a sibling child's, so the
   * match on the section's own url is true on its overview page alone.
   *
   * Descendants count too, not only children: Integrations holds the API
   * section, so a reader on an API page is inside both and both must open.
   */
  const isSectionActive = containsPath(props.node, prefix, currentPath);

  /*
   * Controlled, not `defaultOpen`. `isSectionActive` is derived from the
   * pathname, so a soft navigation changed the "default" of an already
   * mounted uncontrolled Collapsible — Base UI ignores that and warns. The
   * open state is owned here instead: the user's own toggles are kept, and
   * navigating into a section re-opens it (set-state-in-render, not an
   * effect, per the React 19.2 convention in AGENTS.md).
   */
  const [open, setOpen] = useState(isSectionActive || !props.node.collapsed);
  const [prevSectionActive, setPrevSectionActive] = useState(isSectionActive);

  if (prevSectionActive !== isSectionActive) {
    setPrevSectionActive(isSectionActive);

    if (isSectionActive) {
      setOpen(true);
    }
  }

  /*
   * Rendered AS the list item: the sidebar menu is a `<ul>`, and the
   * Collapsible root would otherwise put a `<div>` directly inside it, which
   * is invalid markup and drops the row out of the list for screen readers.
   */
  return (
    <Collapsible
      render={<SidebarMenuItem />}
      className={cn('group/collapsible', {
        'group/active': isSectionActive,
      })}
      open={open}
      onOpenChange={setOpen}
    >
      {props.children}
    </Collapsible>
  );
}

function containsPath(
  node: Cms.ContentItem,
  prefix: string,
  currentPath: string,
): boolean {
  return (
    isRouteActive(prefix + '/' + node.url, currentPath) ||
    node.children.some((child) => containsPath(child, prefix, currentPath))
  );
}
